import { useParams } from "react-router-dom";
import { FiUsers } from "react-icons/fi";
import { useEffect, useMemo, useRef, useState } from "react";
import jets from "../data/jets.json";
import "./Details.css";
import Copyright
 from "./Copyright";
 import InteriorGallery from "./InteriorGallery";
 import Contact from "./Contact";
 import { Trans,useTranslation } from "react-i18next";

export default function Details() {
  const { t } = useTranslation("details");
  const { id } = useParams();
  const numericId = useMemo(() => Number(id), [id]);

  const [jet, setJet] = useState(null);
  const [error, setError] = useState(null);

  // Canvas
  const canvasRef = useRef(null);

  // Charger depuis l'import JSON (pas de fetch)
  useEffect(() => {
    const found = Array.isArray(jets) && jets.find((j) => Number(j.id) === numericId);
    if (!found) {
      setError("Jet introuvable");
      setJet(null);
    } else {
      setError(null);
      setJet(found);
    }
  }, [numericId]);

  // Canvas “points” (moitié haute) — démarre quand jet est dispo (le canvas est monté)
  useEffect(() => {
  if (!jet) return;

  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return;

  const ratio = Math.max(1, window.devicePixelRatio || 1);
  const prefersReduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let rafId = 0;
  let points = [];
  let cssW = 0, cssH = 0;
  const parent = canvas.parentElement || canvas;

  // ——— params visuels
  const spacing = 10;     // distance entre points (px CSS)
  const radius  = 2;      // rayon des points
  const baseO   = 0.24;   // opacité de base
  const maxBoost = 0.55;  // boost max ajouté par l’onde

  // ——— params de l’onde périodique
  const speed    = 100;   // px/s – vitesse de la vague
  const sigma    = 130;    // largeur de l’enveloppe (plus grand = vague plus “large”)
  const vertical = true;  // true: vague du haut vers le bas ; false: de gauche à droite

  // ——— ripples souris (petites ondes locales)
  const enableRipples = true;
  const rippleSigma   = 60;
  const rippleBoost   = 0.55;
  const rippleDuration = 1200; // ms
  const ripples = []; // {x,y,t0}

  const setSize = () => {
    const rect = parent.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    canvas.style.width  = rect.width + "px";
    canvas.style.height = rect.height + "px";
    canvas.width  = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    const changed = Math.round(rect.width) !== cssW || Math.round(rect.height) !== cssH;
    cssW = Math.round(rect.width);
    cssH = Math.round(rect.height);
    return changed;
  };

  const buildPoints = () => {
    points = [];
    for (let y = 0; y <= cssH; y += spacing) {
      for (let x = 0; x <= cssW; x += spacing) {
        points.push({ x, y, r: radius });
      }
    }
  };

  // onde “gaussienne” centrée sur une ligne qui se déplace
const waveBoostAt = (p, t) => {
  const len = vertical ? cssH : cssW;
  const phase = (t * speed) / len;
  const crest = (len / 2) * (1 - Math.cos(2 * Math.PI * phase));
  const coord = vertical ? p.y : p.x;
  const d = coord - crest;
  const env = Math.exp(-(d * d) / (2 * sigma * sigma));
  return maxBoost * env;
};

  const rippleBoostAt = (p, t) => {
    let acc = 0;
    for (let i = ripples.length - 1; i >= 0; i--) {
      const { x, y, t0 } = ripples[i];
      const age = t - t0;
      if (age > rippleDuration) { ripples.splice(i, 1); continue; }
      const dx = p.x - x, dy = p.y - y;
      const dist = Math.hypot(dx, dy);
      // onde circulaire qui se propage ~ (age * speed/2)
      const crest = (age / 1000) * (speed / 2);
      const env = Math.exp(-((dist - crest) ** 2) / (2 * rippleSigma * rippleSigma));
      const fade = 1 - age / rippleDuration; // s’éteint dans le temps
      acc += rippleBoost * env * fade;
    }
    return acc;
  };

 const draw = (tSec) => {
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let i = 0; i < points.length; i++) {
    const p = points[i];

    let o = baseO;
    if (!prefersReduce) {
      o += waveBoostAt(p, tSec);
      if (enableRipples) o += rippleBoostAt(p, tSec * 1000);
      if (o > 0.85) o = 0.85;
    }

    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${o})`;
    ctx.fill();
  }
};

  const t0 = performance.now();
  const animate = () => {
    const now = performance.now();
    const tSec = (now - t0) / 1000;
    draw(tSec);
    rafId = requestAnimationFrame(animate);
  };

  // init + 2e passe
  setSize();
  buildPoints();
  draw(0);

  requestAnimationFrame(() => {
    if (setSize()) { buildPoints(); draw(0); }
  });

  rafId = requestAnimationFrame(animate);

  // resize + observer
  const onResize = () => {
    if (setSize()) { buildPoints(); }
  };
  window.addEventListener("resize", onResize);
  const ro = "ResizeObserver" in window ? new ResizeObserver(onResize) : null;
  ro?.observe(parent);

  // ripples : souris (optionnels)
  const onMove = (e) => {
    if (!enableRipples || prefersReduce) return;
    const rect = canvas.getBoundingClientRect();
    ripples.push({ x: e.clientX - rect.left, y: e.clientY - rect.top, t0: performance.now() });
    // éviter d'accumuler trop
    if (ripples.length > 12) ripples.splice(0, ripples.length - 12);
  };
  const onClick = onMove; // même effet au clic
  canvas.addEventListener("mousemove", onMove);
  canvas.addEventListener("click", onClick);

  return () => {
    window.removeEventListener("resize", onResize);
    ro?.disconnect();
    canvas.removeEventListener("mousemove", onMove);
    canvas.removeEventListener("click", onClick);
    cancelAnimationFrame(rafId);
  };
}, [jet]);

  if (error) return <div className="details-error">Erreur : {t("errors.notFound")}</div>;
  if (!jet) return <div className="details-loading">{t("loading")}</div>;

  const heroSrc = jet.images?.hero ?? jet.image;


  return (
    <>
    <div className="details-page">
      {/* HERO */}
      <section className="hero">
        <img
          className="hero-img"
          src={heroSrc}
          alt={jet.name}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/Images/placeholder.jpg";
          }}
        />
        <div className="hero-overlay" />
        <div className="hero-gradient-top" />
        <header className="hero-content">
          <h1 className="hero-title">{jet.name}</h1>
          <div className="hero-meta">
            <span>{jet.manufacturer}</span>
            <span>{jet.year}</span>
            <span className="meta-capacity">
  <FiUsers className="meta-icon" />
  {jet.capacity}
</span>
          </div>
        </header>
      </section>

      {/* SPECS + points en fond */}
      <section className="specs-section">
        <canvas ref={canvasRef} className="specs-canvas" />
        <div className="specs-gap"></div>
        <div className="specs-inner container">
          <div className="specs-card glassy">
            <ul className="specs-grid">
              <li>
                <span>{t("specs.capacity")}</span>
                <strong>{jet.capacity} pax</strong>
              </li>
              {jet.range && (
                <li>
                  <span>{t("specs.range")}</span>
                  <strong>{jet.range}</strong>
                </li>
              )}
              {jet.speed && (
                <li>
                  <span>{t("specs.speed")}</span>
                  <strong>{jet.speed}</strong>
                </li>
              )}
              {jet.hours && (
                <li>
                  <span>{t("specs.hours")}</span>
                  <strong>{jet.hours}</strong>
                </li>
              )}
              <li>
                <span>{t("specs.year")}</span>
                <strong>{jet.year}</strong>
              </li>
              <li>
                <span>{t("specs.manufacturer")}</span>
                <strong>{jet.manufacturer}</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>
        <div className="specs-actions">
  <a
    className="cta-underline"
    href={jet.fact_sheet}
    download
  >
    {t("actions.downloadFactsheet")}
  </a>
</div>
<div className="text-style-description">
       <p>{t("description", { name: jet.name })}</p>
  
</div>
    </div>
     <InteriorGallery
  title={t("interiorTitle")}
  images={jet.images?.interiors ?? []}
  layout={jet.images?.layout ?? null}
  starShift="-3%"   // ajuste à ton goût (ex: "-6%" ou "0")
/>
<div className="separator"></div>
<Contact/>
    <Copyright/>
    </>
  );
}
